// =====================================
// NewRefills.jsx - VERSIÓN CORREGIDA
// =====================================

import React, { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import TotalBar from "./NewItemsComponents/TotalBar";

import {
  useNewRefillsLogic,
  STEP_CONFIG,
} from "../../hooks/useNewRefillsLogic";

// Componentes de UI compartidos
import Navbar from "../navigation/Navbar";
import LoadingScreen from "../ui/LoadingScreen";

// Componentes específicos de NewRefills
import RefillReviewStep from "./NewRefillsComponents/RefillReviewStep";
import MultiAddressOrderSummary from "./NewRefillsComponents/MultiAddressOrderSummary";
import DeliveryDateSelector from "./NewItemsComponents/DeliveryDateSelector";
import DescriptionBox from "./NewItemsComponents/DescriptionBox";

const NewRefills = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Estado local para totales actualizados
  const [updatedTotals, setUpdatedTotals] = useState(null);

  // Obtenemos los datos del 'location.state' que envió UserJugs
  const { refillData, jugsData } = location.state || {};

  // Pasamos los datos obtenidos como argumento al hook
  const logic = useNewRefillsLogic({ refillData, jugsData });

  // ✅ TODOS LOS HOOKS DEBEN ESTAR ANTES DE CUALQUIER RETURN CONDICIONAL

  // Efecto para manejar el retorno desde OrderProcessing
  useEffect(() => {
    if (location.state?.returnToSummary) {
      // Como no tenemos goToStep, podemos usar otra estrategia
      // Por ejemplo, verificar el currentStep
      // O simplemente mostrar el toast ya que el usuario puede navegar manualmente

      if (location.state?.errorMessage) {
        toast.error(location.state.errorMessage, {
          duration: 5000,
          position: "top-center",
        });
      }
    }
  }, [location.state]);

  // ✅ CALLBACK para evitar setState durante render
  const handleTotalsUpdate = useCallback(
    (newTotals) => {
      console.log("NewRefills: Totales actualizados:", newTotals);

      // Usar setTimeout para evitar setState durante render
      setTimeout(() => {
        setUpdatedTotals(newTotals);

        // Actualizar el total en el contexto si está disponible
        if (logic?.setTotal) {
          logic.setTotal(newTotals.total);
        }
      }, 0);
    },
    [logic?.setTotal]
  );

  // AHORA SÍ PODEMOS HACER RETURNS CONDICIONALES

  // Si el hook no devuelve nada (durante redirección), mostrar loading
  if (!logic) {
    return <LoadingScreen />;
  }

  // Desestructurar después de verificar que logic existe
  const {
    currentStep,
    currentStepIndex,
    availableSteps,
    isLoading,
    groupedByAddress,
    totals,
    deliveryDate,
    handleNext,
    handleBack,
    isButtonDisabled,
    getButtonText,
    isSubmitting,
    setTotal,
    newSalesTerms, // ← CAMBIAR: usar esto en lugar de termsAccepted
    setNewSalesTerms,
  } = logic;

  // Mostrar loading mientras se inicializa
  if (isLoading) {
    return <LoadingScreen />;
  }

  // ✅ USAR TOTALES ACTUALIZADOS O LOS DEL HOOK
  const displayTotals = updatedTotals || totals;

  // Función para obtener el título del paso
  const getStepTitle = () => {
    switch (currentStep) {
      case STEP_CONFIG.VALIDATION:
        return "Información de Entrega";
      case STEP_CONFIG.SCHEDULE:
        return "Fecha y franja de entrega";
      case STEP_CONFIG.SUMMARY:
        return "Resumen de tu pedido";
      default:
        return "Recambios";
    }
  };

  // Función para obtener la descripción del paso
  const getStepDescription = () => {
    switch (currentStep) {
      case STEP_CONFIG.VALIDATION:
        return "Confirma las direcciones y botellones para el recambio.";
      case STEP_CONFIG.SCHEDULE:
        return "Selecciona cuándo quieres recibir tus recambios.";
      case STEP_CONFIG.SUMMARY:
        return "Confirma tu pedido y los términos de entrega.";
      default:
        return "Gestiona tus recambios.";
    }
  };

  // Renderizar contenido según el paso actual
  const renderStepContent = () => {
    switch (currentStep) {
      case STEP_CONFIG.VALIDATION:
        return (
          <RefillReviewStep
            groupedByAddress={groupedByAddress}
            totals={displayTotals}
            selectedDeliveryDate={deliveryDate}
            selectedTimeSlot={null} // TODO: Agregar selectedTimeSlot al hook si es necesario
            onTotalsUpdate={handleTotalsUpdate}
          />
        );
      case STEP_CONFIG.SCHEDULE:
        return <DeliveryDateSelector />;
      case STEP_CONFIG.SUMMARY:
        return (
          <MultiAddressOrderSummary
            groupedByAddress={groupedByAddress}
            totals={displayTotals}
            selectedDate={deliveryDate}
            termsAccepted={newSalesTerms} // ← Agregar esta línea
            setTermsAccepted={setNewSalesTerms} // ← Agregar esta línea
          />
        );
      default:
        return (
          <div className="text-center py-8">
            <p className="text-gray-500">Paso no reconocido: {currentStep}</p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen max-h-screen">
      <Navbar type="new-refills" staticPosition={true} />

      <div className="max-w-6xl mx-auto pt-12 pb-8 px-4 2xl:px-0">
        <DescriptionBox
          type="delivery"
          content={{
            number: (currentStepIndex + 1).toString(),
            title: getStepTitle(),
            description: getStepDescription(),
            className: "mt-0 mb-4",
          }}
          showBackButton={true}
          onBack={handleBack}
        />

        {/* Contenido del paso actual */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {renderStepContent()}
          </motion.div>
        </AnimatePresence>

        {/* Barra inferior unificada */}
        <TotalBar
          currentStepIndex={currentStepIndex}
          totalSteps={availableSteps.length}
          handleNext={handleNext}
          isButtonDisabled={isButtonDisabled}
          getButtonText={getButtonText}
          isSubmitting={isSubmitting}
          isCoordsLoading={false}
          totals={displayTotals}
          progress={0}
          showDeliveryBreakdown={true}
          isRefillMode={true}
          termsAccepted={newSalesTerms} // ← Agregar esta línea
          customLabel={
            currentStep === STEP_CONFIG.SUMMARY ? "Total a pagar" : null
          }
        />
      </div>
    </div>
  );
};

export default NewRefills;
